<template>
  <div>
    <div class="row filter-bar pb-2 pb-md-0">
      <slot name="filterSlot" />
    </div>

    <div
      v-if="!stripped"
      class="row">

      <div
        class="col lists-header"
        v-if="selectable || editable">

        <b-button-group class="float-right ml-3">
          <q-icon
            :active="displayStyle === 'list'"
            @click="displayStyle = 'list'"
            width="20px"
            height="20px"
            pack="leftMenu"
            altPack="leftMenu"
            name="listGrey"
            altName="listGreen" />

          <q-icon
            :active="displayStyle === 'grid'"
            @click="displayStyle = 'grid'"
            style="margin-left: 13px;"
            width="20px"
            height="20px"
            pack="leftMenu"
            altPack="leftMenu"
            name="gridGrey"
            altName="gridGreen" />

        </b-button-group>

        <b-button-group
          v-if="selectable && !limitSelection"
          class="float-right mr-3 button-space">

          <input-button
            @click="selectAll"
            btnStyle="link-style"
            pointable
            variant="plainText"
            :inputPlaceholder="$t('Button.selectAll')" />

          <input-button
            @click="deselectAll"
            btnStyle="link-style"
            pointable
            variant="plainText"
            :inputPlaceholder="$t('Button.unselectAll')" />
        </b-button-group>

      </div>
    </div>

    <pagination-navigation 
      v-if="hasPages"
      :paginationInfo="paginationInfo"
      @prevpage="prevPage"
      @nextpage="nextPage"/>
    
    <div class="spacer-list d-none d-md-block" />
    <div class="spacer-list-smaller d-block d-md-none" />
    <div :class="{'post-list-items': true, 'row': true, scrollable}">
      <div
        v-if="isExcluded(item.id)"
        v-for="item in displayList"
        :key="item.id"
        :class="getItemClass(item.id)">
        
        <div v-if="(displayed === item.__typename || displayed === 'all')">
          <div
            class="msg-deleted"
            v-if="item.deleted">

            <p>
              This item has been deleted from the server.
            </p>
          </div>

          <div :class="postStatus(item)">

            <item-post
              :displayStyle="displayStyle"
              :type="getType(item)"
              :image="getImage(item)"
              :title="getTitle(item)"
              :price="getItemsPrice(item)"
              :discount="getItemsDiscount(item)"
              :datePost="getItemsDateToPost(item)"
              :dateDiscount="getDiscountValidity(item)"
              :url="getItemsUrl(item)"
              :uid="getItemsUid(item)"
              :ref="getItemsUid(item)"
              :postSelected="isSelected(item.id)"
              :content="getItemsContent(item)"
              :imported="item.type === 'importedProduct'"
              :selectable="selectable"
              :editable="editable"
              :deleted="item.deleted"
              @selected="selectPost"
              @editPost="updatePost"
              @deletePost="deletePost" />

          </div>
        </div>
      </div>
    </div>

    <pagination-navigation 
      v-if="hasPages"
      class="pagination-bottom"
      :paginationInfo="paginationInfo"
      @prevpage="prevPage"
      @nextpage="nextPage"/>

  </div>
</template>

<script>
import InputButton from '@/components/standard/input/InputButton'
import ItemPost from '@/components/service/postList/ItemPost'
import PaginationNavigation from '@/components/standard/pagination/PaginationNavigation'
import QIcon from '@/components/standard/display/QIcon'
import moment from 'moment'

export default {
  data () {
    return {
      displayStyle: 'grid',
      selectedPosts: this.selected,
      displayFilter: [
        {
          text: this.$t('services.PostList.select.sort'),
          val: 'all'
        },
        {
          text: this.$t('services.PostList.select.all'),
          val: 'all'
        },
        {
          text: 'Imported',
          val: 'AdvertisableImportedProduct'
        },
        {
          text: this.$t('services.PostList.select.service'),
          val: 'AdvertisableProduct'
        },
        {
          text: this.$t('services.PostList.select.news'),
          val: 'AdvertisablePost'
        }
      ],
      selectItems: [
        {
          text: this.$t('services.PostList.dropDown.service'),
          to: 'createService'
        },
        {
          text: this.$t('services.PostList.dropDown.news'),
          to: 'createPost'
        }
      ],
      displayed: 'all'
    }
  },

  computed: {
    hasPages () {
      return this.paginationInfo.canStepForth || this.paginationInfo.canStepBack
    }
  },

  methods: {
    getTotalLabel (total) {
      if (total < 0) return '?'
      return total
    },

    getType (item) {
      return item.__typename || item.type
    },

    getTitle (item) {
      return item.label
    },

    getItemsDiscount (item) {
      const discount = (item.meta || {}).discount || item.discount
      const discountValidity = (item.meta || {}).discountValidity

      if (!discountValidity) {
        return discount
      }

      // Discount has expired
      if (moment().isAfter(moment(discountValidity))) {
        return undefined
      }

      return discount
    },

    getItemsDateToPost (item) {
      return item.datePost
    },

    getDiscountValidity(item) {
      return item.dateDiscount
    },

    getItemsUrl (item) {
      return item.url
    },

    getItemsUid (item) {
      return item.id
    },

    getItemsContent (item) {
      return item.description
    },

    getItemsPrice (item) {
      let meta = item.meta || {}
      let price = meta.price || item.price
      return price
    },

    getImage (item) {
      let mediaList = (item.media || {}).set || item.images || [] 
      if (mediaList.length < 0) {
        return ''
      }

      return (mediaList[mediaList.length - 1] || {}).url || mediaList[mediaList.length - 1] || ''
    },
  
    isSelected (id) {
      return !!(this.selectedPosts.filter(post => post === id).length)
    },
  
    prevPage () {
      this.$emit('requestPrevPage', {request: true})
    },

    nextPage () {
      this.$emit('requestNextPage', {request: true})
    },

    clearSelection () {
      this.$set(this, 'selectedPosts', [])
    },

    isExcluded (id) {
      let index = this.exclude.findIndex( e => e === id)
      return !(index > -1)
    },

    // Set the post's shading based on it's status:
    // * Fetched localy,
    // * Pulled from remote,
    // * Deleted from remote, still pressent in this sesion...
    postStatus (item) {
      let classes = `section-base`
      // local-ol
      
      if (this.isSelected(item.id)) classes = `${classes} selected-bg`

      if (item.pulled) {
        // classes = `${classes} pulled-ol`
      }

      if (item.deleted) {
        // classes = `${classes} deleted-ol`
      }

      return classes
    },
    getItemClass (id) {
      let base = `post-list-item m-0`

      if (this.isSelected(id)) {
        base += ` selected`
      }

      if (this.displayStyle === 'list') {
        base += ' list-display col-12 m-0 p-0'
      } else if (this.displayStyle === 'grid') {
        base += ' grid-display col-12 col-sm-6'
        if (!this.biggerBoxes) {
          base += ' col-md-4 col-lg-3'
        }
      }
      return base
    },

    isSelected (id) {
      return this.selectedPosts.filter(p => p === id).length > 0
    },

    deselectAll () {
      for (let i = this.selectedPosts.length - 1; i >= 0; i--) {
        let post = this.selectedPosts[i]
        let emit = {
          selected: false,
          id: post
        }
        this.emitPost(this.selectedPosts, emit)
        this.$refs[post][0].forceSelectionParam(false)
      }

      this.selectedPosts = []
    },

    selectAll () {
      let all = []
      for (let post of this.displayList) {
        if (!this.isSelected(post.id)) {
          let emit = {
            selected: true,
            id: post.id
          }
          this.emitPost(this.selectedPosts, emit)
  
          all.push(post.id)
          this.$refs[post.id][0].forceSelectionParam(true)
        }
      }
      this.selectedPosts = all
    },

    emitPost (selectedPosts, changed, advertisable = null) {
      this.$emit('selectionChanged', {
        posts: selectedPosts,
        changed,
        advertisable
      })
    },

    selectPost (e) {
      if (!e.selected) {
        for (let i = this.selectedPosts.length - 1; i >= 0; i--) {
          if (this.selectedPosts[i] === e.id) {
            this.selectedPosts.splice(i, 1)
          }
        }
      }
      else {
        if (this.limitSelection !== null) {
          this.selectedPosts.splice(0, this.selectedPosts.length - this.limitSelection + 1)
        }

        this.selectedPosts.push(e.id)
      }

      let changed = {
        id: e.id,
        selected: e.selected
      }

      this.emitPost(this.selectedPosts, changed, e.advertisable)
    },

    updatePost (e) {
      this.$emit('updatePost', e)
    },

    deletePost (e) {
      this.$emit('deletePost', e)
    },
    
    updatePostList (e) {
      this.posts = e
    },

    changeFilter (e) {
      this.displayed = e.value
    }
  },

  mounted () {
    for (let selected of this.selected) {
      selected = (this.$refs[selected] || [])[0]
      if (selected) {
        selected.forceSelectionParam(true)
      }
    }
  },

  props: {
    biggerBoxes: {
      type: Boolean,
      required: false,
      default: false
    },
    paginationInfo: {
      type: Object,
      required: false,
      default: () => {return {}}
    },
    exclude: {
      type: Array,
      required: false,
      default: () => []
    },
    selected: {
      type: Array,
      required: false,
      default: () => []
    },
    limitSelection: {
      type: Number,
      required: false,
      default: null
    },
    displayList: {
      type: Array,
      required: true
    },
    selectable: {
      type: Boolean,
      required: false,
      default: false
    },
    editable: {
      type: Boolean,
      required: false,
      default: false
    },
    stripped: {
      type: Boolean,
      required: false,
      default: false
    },
    scrollable: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  watch: {
    selected (val, oldVal) {
      this.selectedPosts = val
    }
  },

  components: {
    'q-icon': QIcon,
    'input-button': InputButton,
    'item-post': ItemPost,
    'pagination-navigation': PaginationNavigation
  }
}
</script>

<style scoped>
.button-space {
  margin-top: -6px;
}
.spacer-list {
  margin-top: 14px;
}
.spacer-list-smaller {
  margin-top: 5px;
}

.grid-display {
  padding-left: 7px;
  padding-right: 7px;
  padding-bottom: 20px;
}


.post-list-items {
  padding: 14px 7px 14px 7px;
  background-color: #FFF;
}
.post-list-items.scrollable {
  padding: 14px 7px 14px 7px;
  background-color: #FFF;
  overflow-y: scroll;
  max-height: 61.5vh;
}

.lists-header {
  background-color: #f8f8f8;
  border: 1px solid #E3E3E3;
  padding: 15px;
}
.filter-bar {
  border: 1px solid #D2D3D4;

  padding: 0;
  margin-top: 0;
  margin-bottom: 20px;
  background-color: #ffffff;
}
.lists-header .btn-group{
  cursor: pointer;
}
.link-style{
  font-size: 13px;
}
.pagination-bottom {
  margin-top: 14px;
}
.post-list-item {
  margin-right: 7px;
  margin-left: 7px;
}
.section-base {
  padding-top: 1px;
  padding-bottom: 1px;
  outline: none;
  background-color: transparent;
  transition: outline .3s, background-color .3s;
}
.local-ol {
  outline: 1px solid #0198E1;
}
.pulled-ol {
  outline: 1px solid rgba(50, 255, 100, 1);
}
.deleted-ol {
  outline: 1px solid rgba(255, 50, 100, 1);
  filter: blur(5px);
  transition: filter .1s;
}
.deleted-ol:hover {
  filter: blur(0px);
}
.importable-item:hover .msg-deleted {
  opacity: 0;
}
.msg-deleted {
  opacity: 1;
  position: absolute;
  z-index: 1;
  font-weight: bold;
  padding: 15px;
  transition: opacity .3s;
}
.selected-bg {
  background-color: rgba(50, 255, 100, 0.5);
  box-shadow: 0 0 10px 6px #FAFAFA inset;
}
.items-services {
  display: inline-block;
  /* width: 45%; */
  padding: 0;
}
.space-top {
  margin-top: 6px;
}

.list-display {
  margin: 0;
  width: 100%;
  padding-left: 7px!important;
  padding-right: 7px!important;
}

@media (max-width: 768px) {
  .lists-header {
    border: 0;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/standard/display/PostList.vue?d320f38a